'use client';

import { useEffect, useState } from 'react';
import { notFound } from 'next/navigation';

import {
  useFeatureGuard,
  type FeatureGuardDeniedAction,
} from '@/feature-flags/hooks/use-feature-guard.hook';
import type { BooleanFeatureName } from '@/feature-flags/consts/features.const';
import { logInfo } from '@/feature-flags/utils/logger';
import { getCurrentEvaluationId } from '@/feature-flags/utils/correlation';
import { getCurrentRouteContext } from '@/feature-flags/utils/route-context';

interface FeatureGuardProps {
  feature: BooleanFeatureName;
  children: React.ReactNode;
  requireAuth?: boolean;
  onDenied?: FeatureGuardDeniedAction;
  redirectTarget?: string;
  fallback?: React.ReactNode;
  showFallbackDuringLoading?: boolean;
}

export function FeatureGuard({
  feature,
  children,
  requireAuth = true,
  onDenied = 'redirect',
  redirectTarget = '/',
  fallback = null,
  showFallbackDuringLoading = false,
}: FeatureGuardProps) {
  const { granted, loading } = useFeatureGuard(feature, { requireAuth, onDenied, redirectTarget });

  const [wasGranted, setWasGranted] = useState(false);

  useEffect(() => {
    if (granted) {
      setWasGranted(true);
    }
  }, [granted]);

  useEffect(() => {
    if (loading) {
      return;
    }

    const routeContext = getCurrentRouteContext();

    if (granted) {
      logInfo('Feature access granted', {
        feature_flag: {
          event: 'feature_access_granted',
          evaluation_id: getCurrentEvaluationId(),
          feature,
          route: routeContext.route,
          source: 'FeatureGuard',
        },
      });
    } else {
      logInfo('Feature access denied', {
        feature_flag: {
          event: 'feature_access_denied',
          evaluation_id: getCurrentEvaluationId(),
          feature,
          route: routeContext.route,
          fallback_rendered: !!fallback,
          source: 'FeatureGuard',
        },
      });
    }
  }, [granted, loading, feature, fallback]);

  if (granted || wasGranted) {
    return <>{children}</>;
  }

  if (loading) {
    return showFallbackDuringLoading && fallback ? <>{fallback}</> : null;
  }

  if (onDenied === 'notFound') {
    notFound();
  }

  return fallback ? <>{fallback}</> : null;
}
